'use client';
import { Box, Typography, Card, CardContent, Grid, Chip, Button, Tabs, Tab } from '@mui/material';
import { useState } from 'react';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { useCreateCourseInterest, useGetCourseInterest } from '@/lib/api/course-interest.ts';

const YOUTUBE_VIDEO_ID = import.meta.env.VITE_YOUTUBE_VIDEO_ID || 'm-QJELpmBZA';
const embedUrl = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1`;

const InvestorTrainingPage = () => {
  const modules = [
    {
      id: 1,
      title: 'Mentalidad',
      description: 'Desarrolla la mentalidad correcta para invertir en inmuebles',
    },
    {
      id: 2,
      title: 'Objetivos de inversión',
      description: 'Define tus metas y estrategias de inversión',
    },
    {
      id: 3,
      title: 'Estrategias de inversión',
      description: 'Conoce las diferentes estrategias del mercado inmobiliario',
    },
    {
      id: 4,
      title: 'Burocracia administrativa',
      description: 'Domina los trámites y documentación necesaria',
    },
    {
      id: 5,
      title: 'Búsqueda de activos',
      description: 'Aprende a identificar y evaluar oportunidades',
    },
    {
      id: 6,
      title: 'Negociación',
      description: 'Técnicas para negociar el mejor precio y condiciones',
    },
    { id: 7, title: 'Financiación', description: 'Opciones de financiación y optimización fiscal' },
    { id: 8, title: 'Reforma/adecuación', description: 'Planificación y gestión de reformas' },
    {
      id: 9,
      title: 'Alquiler del activo',
      description: 'Estrategias de alquiler y maximización de ingresos',
    },
    {
      id: 10,
      title: 'Gestión de inquilinos',
      description: 'Selección y gestión eficaz de inquilinos',
    },
    { id: 11, title: 'Venta del activo', description: 'Timing y estrategias de venta' },
    {
      id: 12,
      title: 'Fiscalidad',
      description: 'Optimización fiscal en inversiones inmobiliarias',
    },
  ];

  const { show: showSnackbar } = useSnackbar();
  const { mutateAsync, isPending } = useCreateCourseInterest();
  const { data: courseInterest } = useGetCourseInterest('INVESTOR_TRAINING');
  const [tabIndex, setTabIndex] = useState(0);

  const hasInterest = courseInterest !== null;

  const handleRegisterInterest = async () => {
    try {
      await mutateAsync('INVESTOR_TRAINING');
      showSnackbar({
        title: '¡Interés guardado!',
        message: 'Nos pondremos en contacto contigo.',
        variant: 'success',
      });
    } catch (e) {
      showSnackbar({
        title: 'Error',
        message: 'No se pudo registrar tu interés',
        variant: 'error',
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ mb: 1.5, fontWeight: 700 }}>
          Formación
        </Typography>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Presentación" />
          <Tab label="Contenidos" />
        </Tabs>
      </Box>

      {tabIndex === 0 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: '56.25%',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                component="iframe"
                src={embedUrl}
                title="Vídeo de presentación"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color={'primary'}
              size="large"
              onClick={handleRegisterInterest}
              disabled={isPending || hasInterest}
              sx={{ px: 6, py: 1.5, fontSize: '1.1rem' }}
            >
              {hasInterest ? 'Te avisaremos cuando haya novedades' : 'Me interesa'}
            </Button>
          </Box>
        </Box>
      )}

      {tabIndex === 1 && (
        <Grid container spacing={3}>
          {modules.map((module) => (
            <Grid item xs={12} md={6} lg={4} key={module.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={`Módulo ${module.id}`}
                      size="small"
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {module.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {module.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default InvestorTrainingPage;
